import { useState, useEffect } from 'react';
import api from '../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Separator } from './ui/separator';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Video, MoreVertical, Pencil, Trash2, Copy, XCircle, Star, Users, FileText, DollarSign, ChevronUp, ChevronDown, RefreshCw, Download, ArrowLeft, Loader2 } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';
import { formatDate, formatTime, formatLocalDate, formatTimeLocal, getLocalDayMonth, getLocalWeekday, getStatusColor, getDurationLabel, getInitials, uniqueAttendanceCount } from '../lib/utils';
import { toast } from 'sonner';
import { useAuth } from '../lib/auth-context';
import UserDetailModal from './UserDetailModal';

export default function SessionDetailModal({ session, open, onClose, onUpdate, onEdit }) {
  const { user } = useAuth();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('main'); // main | attendance | recordings | feedback | rename | delete
  const [summaryExpanded, setSummaryExpanded] = useState(false);
  const [feedback, setFeedback] = useState([]);
  const [newTitle, setNewTitle] = useState('');
  const [fetchingTranscript, setFetchingTranscript] = useState(false);
  const [recordingData, setRecordingData] = useState(null);
  const [downloadingRec, setDownloadingRec] = useState(false);
  // V14.5 — learner-side rating state (immutable once submitted)
  const [myRating, setMyRating] = useState(null);       // {submitted, rating, comments}
  const [ratingHover, setRatingHover] = useState(0);
  const [ratingValue, setRatingValue] = useState(0);
  const [ratingComment, setRatingComment] = useState('');
  const [ratingSubmitting, setRatingSubmitting] = useState(false);
  // V14.5 Part 18b — Admin can click an LMS-joined attendee row in the
  // Session Attendance modal to open the existing UserDetailModal layered
  // on top. Public-link joiners (joined_via !== 'lms') stay non-navigable
  // and show a toast explaining why.
  const [attendeeUserId, setAttendeeUserId] = useState(null);

  const isAdmin = user?.role === 'superadmin' || user?.role === 'admin';
  const isLearner = user?.role === 'learner';

  useEffect(() => {
    if (open && session?.id) {
      setLoading(true);
      setView('main');
      setSummaryExpanded(false);
      setRecordingData(null);
      setMyRating(null);
      setRatingHover(0);
      setRatingValue(0);
      setRatingComment('');
      api.get(`/api/sessions/${session.id}/detail`)
        .then(res => setDetail(res.data))
        .catch(() => setDetail(null))
        .finally(() => setLoading(false));
      // V14.5 — Fetch the learner's own rating for this session so we can
      // render it immutably (cannot be changed once submitted).
      if (isLearner) {
        api.get(`/api/feedback/check/${session.id}`)
          .then(res => setMyRating(res.data))
          .catch(() => setMyRating({ submitted: false }));
      }
    }
  }, [open, session?.id, isLearner]);

  if (!session) return null;
  const d = detail || session;
  const attendance = d.attendance || [];
  const aiSummary = d.ai_summary || '';
  const topicsCovered = d.topics_covered || [];

  // V14.5 Part 19p — single source of truth for "X present".
  // Outer card was showing raw `attendance.length` (rejoins included),
  // inside modal was showing the consolidated unique count. Founder
  // reported "17 outside vs 13 inside" — same fix everywhere now via
  // the shared `uniqueAttendanceCount` helper in lib/utils.js.
  const uniquePresentCount = uniqueAttendanceCount(attendance);

  const getEndTimeStr = (time, dur) => {
    if (!time) return '';
    const [h, m] = time.split(':').map(Number);
    const totalMin = h * 60 + m + (dur || 60);
    const eh = Math.floor(totalMin / 60) % 24;
    const em = totalMin % 60;
    const ampm = eh >= 12 ? 'PM' : 'AM';
    return `${eh % 12 || 12}:${String(em).padStart(2, '0')} ${ampm}`;
  };

  const handleRename = async () => {
    try {
      await api.post(`/api/sessions/${session.id}/rename?title=${encodeURIComponent(newTitle)}`);
      toast.success('Session renamed');
      setView('main');
      onUpdate?.();
    } catch { toast.error('Failed to rename'); }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/api/sessions/${session.id}`);
      toast.success('Session deleted');
      onClose();
      onUpdate?.();
    } catch { toast.error('Failed to delete'); }
  };

  const fetchFeedback = async () => {
    try {
      const res = await api.get(`/api/feedback/session/${session.id}`);
      setFeedback(res.data.feedback || res.data || []);
      setView('feedback');
    } catch { toast.error('Failed to load feedback'); }
  };

  const fetchTranscript = async () => {
    setFetchingTranscript(true);
    try {
      const res = await api.post(`/api/sessions/${session.id}/fetch-transcript`);
      toast.success(res.data.message || 'Transcript fetched');
      // Reload detail
      const detailRes = await api.get(`/api/sessions/${session.id}/detail`);
      setDetail(detailRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to fetch transcript');
    }
    setFetchingTranscript(false);
  };

  const loadRecordings = async () => {
    try {
      const res = await api.get(`/api/sessions/${session.id}/recording`);
      setRecordingData(res.data);
      setView('recordings');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No recording available');
    }
  };

  // Admin-only: download the completed class recording as an MP4.
  // We mint a short-lived purpose-locked token, then let the browser
  // stream-download natively (Content-Disposition: attachment) so large
  // recordings never get buffered into memory.
  const downloadRecording = async () => {
    if (downloadingRec) return;
    setDownloadingRec(true);
    try {
      const res = await api.post(`/api/sessions/${session.id}/recording/download-token`);
      const token = res.data?.download_token;
      if (!token) throw new Error('no token');
      const base = process.env.REACT_APP_BACKEND_URL || '';
      const url = `${base}/api/sessions/${session.id}/recording/download?dt=${encodeURIComponent(token)}`;
      const a = document.createElement('a');
      a.href = url;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success('Preparing your download…');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Recording download is not available yet');
    } finally {
      setDownloadingRec(false);
    }
  };

  // ─── Render based on current view ───
  const renderContent = () => {
    // ─── ATTENDANCE VIEW ───
    if (view === 'attendance') {
      const isClassEnded = d.status === 'completed';
      // V14.3 — Consolidate raw join/leave events to unique attendees up-front
      // so the badge count and the table render the SAME number. Previously
      // the badge showed `attendance.length` (raw events = 7) while the table
      // rendered the consolidated unique attendees (4) — confusing mismatch.
      const consolidated = (() => {
        const groups = new Map();
        for (const att of attendance) {
          const key = (att.email || att.lms_user_id || att.user_name || att.lms_user_name || `idx-${groups.size}`).toString().toLowerCase();
          if (!groups.has(key)) {
            groups.set(key, {
              name: att.lms_user_name || att.user_name || 'Unknown',
              email: att.email || '',
              joined_via: att.joined_via,
              is_trial: !!att.is_trial,
              // V14.5 Part 18b — capture LMS user_id so clicks on the row
              // can open the existing UserDetailModal. Falls back to the
              // first attendance event that has it.
              lms_user_id: att.lms_user_id || null,
              segments: [],
            });
          } else {
            // First-seen wins, but fill missing lms_user_id if a later
            // event for the same group happens to carry it.
            const g = groups.get(key);
            if (!g.lms_user_id && att.lms_user_id) g.lms_user_id = att.lms_user_id;
          }
          groups.get(key).segments.push({
            join: att.join_time ? new Date(att.join_time) : null,
            leave: att.leave_time ? new Date(att.leave_time) : null,
          });
        }
        return [...groups.values()].map((g) => {
          g.segments.sort((a, b) => (a.join?.getTime() || 0) - (b.join?.getTime() || 0));
          const firstJoin = g.segments.find((s) => s.join)?.join;
          const last = g.segments[g.segments.length - 1];
          const inSession = last && !last.leave && !isClassEnded;
          const lastExit = inSession ? null : (
            [...g.segments].reverse().find((s) => s.leave)?.leave
          );
          let totalMs = 0;
          const cap = isClassEnded
            ? (d.actual_end_time ? new Date(d.actual_end_time) : new Date())
            : new Date();
          for (const s of g.segments) {
            if (!s.join) continue;
            const end = s.leave || cap;
            totalMs += Math.max(0, end.getTime() - s.join.getTime());
          }
          const durationMin = Math.round(totalMs / 60000);
          return { ...g, firstJoin, lastExit, inSession, durationMin };
        });
      })();
      const hasRejoins = attendance.length > consolidated.length;
      return (
        <>
          <DialogHeader>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setView('main')}><ArrowLeft className="h-4 w-4" /></Button>
              <DialogTitle className="font-heading text-lg">Session Attendance</DialogTitle>
            </div>
          </DialogHeader>
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <Badge variant="secondary" data-testid="attendance-present-badge">{consolidated.length} present</Badge>
            {hasRejoins && (
              <Badge variant="outline" className="text-[10px] text-muted-foreground" data-testid="attendance-rejoin-badge">
                {attendance.length} total joins (incl. rejoins)
              </Badge>
            )}
            {d.status === 'live' && <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse mr-1 inline-block" />Live</Badge>}
            {d.status === 'live' && (
              <Button variant="outline" size="sm" className="ml-auto" onClick={() => {
                api.get(`/api/sessions/${session.id}/detail`).then(res => setDetail(res.data));
              }}><RefreshCw className="h-3 w-3 mr-1" />Refresh</Button>
            )}
          </div>
          {d.status === 'live' && (
            <div className="flex gap-2 mb-3">
              <Button size="sm" onClick={async () => {
                const r = await api.get(`/api/sessions/${session.id}/link`);
                if (r.data.zoom_start_url) window.open(r.data.zoom_start_url, '_blank');
                else toast.error(r.data.start_blocked_reason || 'Cannot start');
              }}>Join as Host</Button>
              <Button size="sm" variant="outline" onClick={async () => {
                const r = await api.get(`/api/sessions/${session.id}/link`);
                if (r.data.zoom_join_url) window.open(r.data.zoom_join_url, '_blank');
              }}>Join as Learner</Button>
            </div>
          )}
          {attendance.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">No attendance data yet</p>
          ) : (() => {
            return (
              <Table data-testid="attendance-table">
                <TableHeader><TableRow>
                  <TableHead className="text-xs">Name</TableHead>
                  <TableHead className="text-xs">Status</TableHead>
                  <TableHead className="text-xs">Join time</TableHead>
                  <TableHead className="text-xs">Exit time</TableHead>
                  {isClassEnded && <TableHead className="text-xs">Duration</TableHead>}
                  <TableHead className="text-xs">Via</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {consolidated.map((g, i) => {
                    const fmt = (dt) => dt ? dt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '-';
                    const isHost = i === 0 || g.name.toLowerCase() === (d.instructor_name || '').toLowerCase();
                    // V14.5 Part 18b — Click opens UserDetailModal for LMS
                    // joiners; for public-link joiners we surface a toast
                    // so admins know exactly why nothing happens.
                    const isLmsJoiner = g.joined_via === 'lms' && !!g.lms_user_id;
                    const handleRowClick = () => {
                      if (isLmsJoiner) {
                        setAttendeeUserId(g.lms_user_id);
                      } else {
                        toast.info('Joined via public link — this learner did not sign in to the LMS, so there is no profile to open.');
                      }
                    };
                    return (
                      <TableRow
                        key={g.email || g.name + i}
                        className={`${isHost ? 'bg-primary/5' : ''} ${isLmsJoiner ? 'cursor-pointer hover:bg-muted/50' : 'cursor-help'} transition-colors`}
                        onClick={handleRowClick}
                        title={isLmsJoiner ? 'Click to view learner profile' : 'Joined via public link — not signed into LMS'}
                        data-testid={`attendance-row-${i}`}
                      >
                        <TableCell className="text-xs flex items-center gap-1.5">
                          <Avatar className="h-5 w-5"><AvatarFallback className={`text-[8px] ${isHost ? 'bg-primary text-white' : 'bg-primary/10 text-primary'}`}>{getInitials(g.name)}</AvatarFallback></Avatar>
                          <span className={`${isHost ? 'font-semibold' : ''} ${isLmsJoiner ? 'underline-offset-2 hover:underline' : ''}`}>{g.name}</span>
                          {isHost && <Badge className="bg-primary text-white text-[8px] px-1 py-0 border-0">Host</Badge>}
                        </TableCell>
                        <TableCell>
                          {g.inSession ? (
                            <Badge variant="outline" className="text-[9px] bg-emerald-50 text-emerald-700 border-emerald-200">
                              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse mr-1 inline-block" />
                              In-session
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-[9px] bg-slate-50 text-slate-600 border-slate-200">Left</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-xs">{fmt(g.firstJoin)}</TableCell>
                        <TableCell className="text-xs">{g.inSession ? '—' : fmt(g.lastExit)}</TableCell>
                        {isClassEnded && (
                          <TableCell className="text-xs">{g.durationMin} min</TableCell>
                        )}
                        <TableCell><Badge variant="outline" data-testid="attendance-via-badge" className={`text-[9px] ${g.is_trial ? 'bg-amber-50 text-amber-800 border-amber-200' : g.joined_via === 'lms' ? 'bg-emerald-50 text-emerald-700' : 'bg-blue-50 text-blue-700'}`}>{g.is_trial ? 'Trial' : g.joined_via === 'lms' ? 'LMS' : 'Zoom'}</Badge></TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            );
          })()}
        </>
      );
    }

    // ─── RECORDINGS VIEW ───
    if (view === 'recordings') {
      const bunnyVideos = recordingData?.bunny_videos || [];
      const readyVideos = bunnyVideos.filter(v => v.status === 'ready').filter(v => !isLearner || v.view_type === 'speaker');
      const hasReady = readyVideos.length > 0;
      return (
        <>
          <DialogHeader>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setView('main')}><ArrowLeft className="h-4 w-4" /></Button>
              <DialogTitle className="font-heading text-lg">Session Recordings</DialogTitle>
              {isAdmin && (
                <Button variant="outline" size="sm" className="ml-auto" onClick={downloadRecording} disabled={downloadingRec} data-testid="download-recording-btn-recordings-view">
                  {downloadingRec ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Download className="h-3.5 w-3.5 mr-1" />}
                  {downloadingRec ? 'Preparing…' : 'Download'}
                </Button>
              )}
            </div>
          </DialogHeader>
          {recordingData?.source === 'bunny' && hasReady ? (
            <div className="space-y-3">
              {/* View type tabs when multiple recordings */}
              {readyVideos.length > 1 ? (
                <RecordingTabs videos={readyVideos} />
              ) : (
                readyVideos.map(v => (
                  <div key={v.bunny_guid}>
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant="outline" className="capitalize">{v.view_type} View</Badge>
                    </div>
                    <div className="relative w-full rounded-xl overflow-hidden bg-black" style={{ paddingTop: '56.25%' }}>
                      {/* V14.5 — `loading="eager"` (was `lazy`) so the iframe initialises
                          even inside a dialog with overflow-y-auto where the
                          IntersectionObserver fails on mobile. `preload=true`
                          tells the Bunny player to fetch poster + first frame
                          so the play button is visible (otherwise mobile shows
                          a pure black box until tap).
                          `playsinline=true` prevents iOS Safari from forcing
                          fullscreen native player on tap. */}
                      <iframe
                        src={v.embed_url + (v.embed_url.includes('?') ? '&' : '?') + 'preload=true&autoplay=false&playsinline=true&responsive=true'}
                        loading="eager"
                        title={`${v.view_type} view recording`}
                        style={{ border: 'none', position: 'absolute', top: 0, left: 0, height: '100%', width: '100%' }}
                        allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  </div>
                ))
              )}
            </div>
          ) : recordingData?.source === 'bunny_processing' ? (
            <div className="text-center py-8" data-testid="recording-processing">
              <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-2" />
              <p className="text-sm">Recording is being processed...</p>
              <p className="text-xs text-muted-foreground mt-1">
                {recordingData?.ingest_kicked
                  ? 'Just queued — the inline player will be ready in 1-3 minutes.'
                  : 'This may take a few minutes. Refresh to check again.'}
              </p>
              <Button variant="outline" size="sm" className="mt-3" onClick={loadRecordings}>
                <RefreshCw className="h-3.5 w-3.5 mr-1" />Refresh
              </Button>
            </div>
          ) : recordingData?.recording_url && isAdmin ? (
            // Admin-only fallback to Zoom while Bunny ingest figures itself out
            <div className="text-center py-6" data-testid="recording-zoom-fallback">
              <Video className="h-8 w-8 text-blue-600 mx-auto mb-2" />
              <p className="text-sm font-medium">Zoom Recording Available</p>
              <p className="text-xs text-muted-foreground mt-1">
                Bunny ingest hasn't completed for this session yet — click below for the original Zoom recording. Learners will see the inline player once Bunny is ready.
              </p>
              <a href={recordingData.recording_url} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" size="sm" className="mt-2"><Video className="h-3.5 w-3.5 mr-1" />Watch on Zoom</Button>
              </a>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-6" data-testid="recording-unavailable">
              {isLearner
                ? 'Recording is being prepared. Please check back in a few minutes.'
                : 'No recordings available'}
            </p>
          )}
        </>
      );
    }

    // ─── FEEDBACK VIEW ───
    if (view === 'feedback') {
      return (
        <>
          <DialogHeader>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setView('main')}><ArrowLeft className="h-4 w-4" /></Button>
              <DialogTitle className="font-heading text-lg">Ratings & Feedback</DialogTitle>
            </div>
          </DialogHeader>
          {feedback.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">No feedback submitted yet</p>
          ) : (
            <div className="space-y-3">
              {feedback.map((f, i) => (
                <div key={i} className="p-3 rounded-xl border">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-sm font-medium">{f.user_name || 'Learner'}</p>
                    <div className="flex gap-0.5">{[1,2,3,4,5].map(s => <Star key={s} className={`h-3.5 w-3.5 ${s <= (f.rating||0) ? 'text-amber-500 fill-amber-500' : 'text-muted-foreground/30'}`} />)}</div>
                  </div>
                  {f.comment && <p className="text-xs text-muted-foreground">{f.comment}</p>}
                </div>
              ))}
            </div>
          )}
        </>
      );
    }

    // ─── RENAME VIEW ───
    if (view === 'rename') {
      return (
        <>
          <DialogHeader><DialogTitle>Rename Session</DialogTitle></DialogHeader>
          <Input value={newTitle} onChange={e => setNewTitle(e.target.value)} placeholder="Session name" />
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setView('main')}>Cancel</Button>
            <Button onClick={handleRename}>Save</Button>
          </div>
        </>
      );
    }

    // ─── DELETE CONFIRM ───
    if (view === 'delete') {
      return (
        <>
          <DialogHeader><DialogTitle>Delete Session</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Permanently delete "{d.title}" on {formatLocalDate(session.scheduled_date, session.scheduled_time)}?</p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setView('main')}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete}>Delete</Button>
          </div>
        </>
      );
    }

    // ─── MAIN VIEW ───
    return (
      <>
        <DialogHeader className="pb-0">
          <DialogTitle className="text-lg font-heading font-semibold">{d.batch_name || 'Session'}</DialogTitle>
          <p className="text-sm text-muted-foreground">{d.batch_course_type ? d.batch_course_type.charAt(0).toUpperCase() + d.batch_course_type.slice(1) + ' Yoga' : ''}</p>
        </DialogHeader>

        <div className="space-y-5 pt-2">
          {/* Session Info */}
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="text-center bg-primary/10 rounded-xl p-2.5 min-w-[52px]">
                {(() => {
                  const dm = getLocalDayMonth(session.scheduled_date, session.scheduled_time);
                  return (<>
                    <p className="text-[10px] text-primary uppercase font-medium">{dm.month}</p>
                    <p className="text-xl font-bold text-primary">{dm.day}</p>
                  </>);
                })()}
              </div>
              <div>
                <p className="font-semibold">{d.title || 'Live Session'}</p>
                <p className="text-sm text-muted-foreground">{getLocalWeekday(session.scheduled_date, session.scheduled_time)}, {formatTimeLocal(session.scheduled_time, session.scheduled_date)} - {getEndTimeStr(session.scheduled_time, session.duration_minutes)}</p>
                {/* V13.9 — show actual duration once the session has ended */}
                {d.status === 'completed' && d.actual_duration_minutes ? (
                  <p className="text-xs text-muted-foreground mt-0.5">
                    <span className="font-medium text-foreground">{getDurationLabel(d.actual_duration_minutes)} actual</span>
                    {d.duration_minutes !== d.actual_duration_minutes && (
                      <> · scheduled {getDurationLabel(d.duration_minutes)}</>
                    )}
                  </p>
                ) : null}
                <div className="flex items-center gap-1.5 mt-1">
                  {d.status === 'completed' && <Badge className="bg-slate-800 text-white text-[10px] border-0">Ended</Badge>}
                  <Badge className={`text-[10px] border-0 ${
                    d.status === 'live' ? 'bg-emerald-600 text-white' :
                    d.status === 'completed' ? 'bg-primary text-primary-foreground' :
                    d.status === 'upcoming' ? 'bg-blue-600 text-white' :
                    d.status === 'canceled' ? 'bg-rose-600 text-white' :
                    'bg-muted text-muted-foreground'
                  }`}>
                    {d.status === 'live' ? <span className="flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-white animate-pulse" />Live</span> : d.status}
                  </Badge>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {d.status === 'completed' && (d.has_recording || (isAdmin && (d.bunny_videos?.length > 0 || d.recording_url))) && (
                <Button variant="outline" size="sm" onClick={loadRecordings} data-testid="play-recording-btn"><Video className="h-3.5 w-3.5 mr-1" />Play Recording</Button>
              )}
              {isAdmin && d.status === 'completed' && (d.has_recording || d.bunny_videos?.length > 0 || d.recording_url) && (
                <Button variant="outline" size="sm" onClick={downloadRecording} disabled={downloadingRec} data-testid="download-recording-btn">
                  {downloadingRec ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Download className="h-3.5 w-3.5 mr-1" />}
                  {downloadingRec ? 'Preparing…' : 'Download'}
                </Button>
              )}
              {/* V14.5 — 3-dot menu hidden for learners (rename/delete/transcript/process-recordings are admin actions). */}
              {!isLearner && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8"><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => { setNewTitle(d.title || ''); setView('rename'); }}><Pencil className="h-4 w-4 mr-2" />Rename</DropdownMenuItem>
                  {d.status === 'upcoming' && <DropdownMenuItem className="text-destructive" onClick={() => setView('delete')}><Trash2 className="h-4 w-4 mr-2" />Delete</DropdownMenuItem>}
                  <DropdownMenuItem onClick={async () => {
                    const r = await api.get(`/api/sessions/${session.id}/link`);
                    await navigator.clipboard.writeText(r.data.zoom_join_url || `Session: ${d.title}`);
                    toast.success('Link copied');
                  }}><Copy className="h-4 w-4 mr-2" />Copy session link</DropdownMenuItem>
                  {isAdmin && d.status === 'completed' && (
                    <>
                      <DropdownMenuItem onClick={async () => {
                        try {
                          const res = await api.get(`/api/sessions/${session.id}/attendance-csv`, { responseType: 'blob' });
                          const url = window.URL.createObjectURL(new Blob([res.data]));
                          const a = document.createElement('a'); a.href = url; a.download = `attendance_${session.id}.csv`; a.click();
                        } catch { toast.error('Failed to download'); }
                      }}><Download className="h-4 w-4 mr-2" />Download attendance</DropdownMenuItem>
                      <DropdownMenuItem onClick={fetchTranscript} disabled={fetchingTranscript}>
                        <FileText className="h-4 w-4 mr-2" />{fetchingTranscript ? 'Fetching...' : 'Fetch transcript + AI Summary'}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={async () => {
                        try {
                          toast.info('Processing recordings...');
                          const res = await api.post(`/api/sessions/${session.id}/process-recordings`);
                          toast.success(res.data.message || 'Recordings processing started');
                          setTimeout(() => { api.get(`/api/sessions/${session.id}/detail`).then(r => setDetail(r.data)); }, 5000);
                        } catch (err) { toast.error(err.response?.data?.detail || 'Failed to process recordings'); }
                      }}>
                        <Video className="h-4 w-4 mr-2" />Process recordings (Bunny upload)
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
              )}
            </div>
          </div>

          <Separator />

          {/* Hosted By */}
          <div className="flex items-center gap-3">
            <Avatar className="h-10 w-10"><AvatarFallback className="bg-primary text-primary-foreground font-semibold">{getInitials(d.instructor_name || 'IN')}</AvatarFallback></Avatar>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Hosted by</p>
              <p className="font-medium">{d.instructor_name || 'Instructor'}</p>
            </div>
          </div>

          {/* SoulAI Summary */}
          {aiSummary && (
            <>
              <Separator />
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <FileText className="h-4 w-4 text-primary" />
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Summary</p>
                  <Badge className="bg-primary/10 text-primary border-primary/20 text-[9px] px-1.5 py-0">SoulAI</Badge>
                </div>
                <p className={`text-sm text-foreground/80 leading-relaxed ${summaryExpanded ? '' : 'line-clamp-3'}`}>{aiSummary}</p>
                {/* Structured sections (when expanded) */}
                {summaryExpanded && (d.ai_sections || []).length > 0 && (
                  <div className="mt-3 space-y-3">
                    {(d.ai_sections || []).map((sec, i) => (
                      <div key={i}>
                        <p className="text-sm font-semibold text-foreground">{sec.heading}</p>
                        <p className="text-sm text-foreground/70 leading-relaxed mt-0.5">{sec.content}</p>
                      </div>
                    ))}
                  </div>
                )}
                {(aiSummary.length > 150 || (d.ai_sections || []).length > 0) && (
                  <button className="text-xs text-primary font-medium mt-2 hover:underline flex items-center gap-1" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setSummaryExpanded(!summaryExpanded); }}>
                    {summaryExpanded ? <><ChevronUp className="h-3 w-3" />Show Less</> : <><ChevronDown className="h-3 w-3" />See More</>}
                  </button>
                )}
              </div>
            </>
          )}

          {/* Topics Covered */}
          {topicsCovered.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Topics Covered</p>
                <Badge className="bg-primary/10 text-primary border-primary/20 text-[9px] px-1.5 py-0">SoulAI</Badge>
              </div>
              <div className="flex flex-wrap gap-1.5">{topicsCovered.map((t, i) => <Badge key={i} variant="outline" className="text-xs">{t}</Badge>)}</div>
            </div>
          )}

          {/* Instructor Payout */}
          {isAdmin && d.payout_amount != null && (
            <>
              <Separator />
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center"><DollarSign className="h-4 w-4 text-muted-foreground" /></div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Instructor Payout</p>
                  <div className="font-medium">INR {d.payout_amount || 0} <Badge variant="outline" className="text-[9px] text-amber-700 bg-amber-50 border-amber-200 ml-1">Review</Badge></div>
                </div>
              </div>
            </>
          )}

          {/* Attendance — admin/instructor only. Learners don't see attendance. */}
          {!isLearner && (
          <>
          <Separator />
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center"><Users className="h-4 w-4 text-muted-foreground" /></div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Attendance</p>
                <p className="font-medium" data-testid="attendance-outer-count">{uniquePresentCount} present</p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={() => setView('attendance')}>View</Button>
          </div>
          </>
          )}

          {/* Rating — V14.5
             • Admin/instructor: read-only aggregate + "View Feedbacks" drill-in
             • Learner (not yet rated, completed session): interactive 1-5 star widget
             • Learner (already rated): immutable display of their own rating + comment.
               The rating CANNOT be changed once submitted. */}
          <Separator />
          {isLearner ? (
            d.status === 'completed' ? (
              myRating?.submitted ? (
                /* Locked, immutable rating */
                <div className="space-y-2" data-testid="learner-rating-locked">
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center"><Star className="h-4 w-4 text-amber-500 fill-amber-500" /></div>
                    <div className="flex-1">
                      <p className="text-xs text-muted-foreground uppercase tracking-wider">Your Rating</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <div className="flex gap-0.5">
                          {[1,2,3,4,5].map(s => {
                            // Backend stores 1-10; normalize to 1-5 stars (5 if rating=10).
                            const stars = Math.round((myRating.rating || 0) / 2);
                            return (
                              <Star key={s} className={`h-4 w-4 ${s <= stars ? 'text-amber-500 fill-amber-500' : 'text-muted-foreground/30'}`} />
                            );
                          })}
                        </div>
                        <Badge variant="outline" className="text-[9px]">Cannot be changed</Badge>
                      </div>
                    </div>
                  </div>
                  {myRating.comments && (
                    <p className="text-sm text-foreground/70 italic pl-11">&ldquo;{myRating.comments}&rdquo;</p>
                  )}
                </div>
              ) : (
                /* Interactive rating widget */
                <div className="space-y-2" data-testid="learner-rating-widget">
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center"><Star className="h-4 w-4 text-muted-foreground" /></div>
                    <div className="flex-1">
                      <p className="text-xs text-muted-foreground uppercase tracking-wider">Rate this class</p>
                      <div className="flex gap-1 mt-1">
                        {[1,2,3,4,5].map(s => (
                          <button
                            key={s}
                            type="button"
                            data-testid={`rating-star-${s}`}
                            onMouseEnter={() => setRatingHover(s)}
                            onMouseLeave={() => setRatingHover(0)}
                            onClick={() => setRatingValue(s)}
                            className="hover:scale-110 transition-transform"
                            aria-label={`Rate ${s} star${s > 1 ? 's' : ''}`}
                          >
                            <Star
                              className={`h-6 w-6 ${(ratingHover || ratingValue) >= s ? 'text-amber-500 fill-amber-500' : 'text-muted-foreground/40'}`}
                            />
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                  {ratingValue > 0 && (
                    <div className="pl-11 space-y-2">
                      <Textarea
                        value={ratingComment}
                        onChange={e => setRatingComment(e.target.value)}
                        placeholder="Optional — what did you like or what could improve? (optional)"
                        className="text-sm min-h-[60px]"
                        data-testid="rating-comment-input"
                      />
                      <p className="text-[11px] text-muted-foreground">Note: once submitted, your rating can&apos;t be changed.</p>
                      <Button
                        size="sm"
                        disabled={ratingSubmitting}
                        data-testid="rating-submit-btn"
                        onClick={async () => {
                          setRatingSubmitting(true);
                          try {
                            // Backend stores 1-10; we multiply 1-5 stars by 2.
                            await api.post('/api/feedback', {
                              session_id: session.id,
                              batch_id: d.batch_id,
                              rating: ratingValue * 2,
                              comments: ratingComment,
                              batch_name: d.batch_name,
                              scheduled_date: session.scheduled_date,
                            });
                            toast.success('Thank you for your rating!');
                            // Re-fetch to lock the UI immutably.
                            const res = await api.get(`/api/feedback/check/${session.id}`);
                            setMyRating(res.data);
                          } catch (err) {
                            toast.error(err.response?.data?.detail || 'Failed to submit rating');
                          }
                          setRatingSubmitting(false);
                        }}
                      >
                        {ratingSubmitting ? 'Submitting...' : 'Submit Rating'}
                      </Button>
                    </div>
                  )}
                </div>
              )
            ) : null /* Don't show rating widget for non-completed sessions */
          ) : (
            /* Admin/instructor: aggregate view */
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center"><Star className="h-4 w-4 text-muted-foreground" /></div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Rating</p>
                  <p className="font-medium">{d.avg_rating ? <><Star className="h-3.5 w-3.5 inline text-amber-500 fill-amber-500 mr-0.5" />{d.avg_rating}</> : 'No ratings yet'}</p>
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={fetchFeedback}>View Feedbacks</Button>
            </div>
          )}
        </div>
      </>
    );
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => { if (!o) { setView('main'); onClose(); } }}>
        <DialogContent className={`${view === 'recordings' ? 'sm:max-w-2xl' : 'sm:max-w-xl'} max-h-[85vh] overflow-y-auto`} data-testid="session-detail-modal">
          {loading ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
          ) : (
            renderContent()
          )}
        </DialogContent>
      </Dialog>

      {/* V14.5 Part 18b — Layered learner-profile modal opened from the
          Session Attendance table. Renders only for admins (UserDetailModal
          itself further enforces role-aware visibility). Closes back to
          the attendance modal. */}
      {isAdmin && (
        <UserDetailModal
          userId={attendeeUserId}
          open={!!attendeeUserId}
          onClose={() => setAttendeeUserId(null)}
          onUpdate={() => { /* attendance modal doesn't need refetch */ }}
        />
      )}
    </>
  );
}

// Helper: Recording tabs for Gallery/Speaker view toggle
function RecordingTabs({ videos }) {
  const [activeTab, setActiveTab] = useState(videos[0]?.view_type || 'speaker');
  const current = videos.find(v => v.view_type === activeTab) || videos[0];
  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        {videos.map(v => (
          <button
            key={v.view_type}
            onClick={() => setActiveTab(v.view_type)}
            className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
              activeTab === v.view_type
                ? 'bg-primary text-white'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            {v.view_type === 'speaker' ? 'Speaker View' : 'Gallery View'}
          </button>
        ))}
      </div>
      {current && (
        <div className="relative w-full rounded-xl overflow-hidden bg-black" style={{ paddingTop: '56.25%' }}>
          {/* V14.5 — same mobile-compat changes as the single-video iframe. */}
          <iframe
            key={current.bunny_guid}
            src={current.embed_url + (current.embed_url.includes('?') ? '&' : '?') + 'preload=true&autoplay=false&playsinline=true&responsive=true'}
            loading="eager"
            title={`${current.view_type} view recording`}
            style={{ border: 'none', position: 'absolute', top: 0, left: 0, height: '100%', width: '100%' }}
            allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture"
            allowFullScreen
          />
        </div>
      )}
    </div>
  );
}
